import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup } from 'webapp-types';
import { FilterLogicalOperator } from 'webapp/enums/enums';
import { compact, uniq } from 'webapp/utils/common';

import FilterItem from 'webapp/models-custom/filter/filter-item';
import Serviced from 'webapp/models-custom/serviced';

export type OnChangeProp = keyof DSSetup.MetricSetupFilterGroupData;
export type OnChangePropValue =
	DSSetup.MetricSetupFilterGroupData[keyof DSSetup.MetricSetupFilterGroupData];

// used to inform MetricSetup about changes on the group (and items, by proxy)
export type OnChangeFn = (_prop: OnChangeProp, _value: OnChangePropValue) => void;

export interface FilterItemProps extends DSSetup.MetricSetupFilterGroupData {
	onChange?: OnChangeFn;
}

const MAX_FILTER_ITEMS = 50;

export default class FilterGroup extends Serviced {
	@tracked private _logicalOperator: FilterLogicalOperator;
	@tracked filterItems: FilterItem[];

	private onChange: OnChangeFn | null = null;

	constructor(data: DSSetup.MetricSetupFilterGroupData, onChange?: OnChangeFn) {
		super();

		this._logicalOperator = data.logicalOperator;
		this.filterItems = data.filterItems.map(
			(d) =>
				new FilterItem(d, () => {
					this.notifyItemsChange();
				}),
		);

		if (onChange) {
			this.onChange = onChange;
		}
	}

	get logicalOperator(): FilterLogicalOperator {
		return this._logicalOperator;
	}

	set logicalOperator(value: FilterLogicalOperator) {
		if (value === this._logicalOperator) {
			return;
		}

		this._logicalOperator = value;
		this.notifyChange('logicalOperator', value);
	}

	get logicalOperatorText() {
		return this.logicalOperator === FilterLogicalOperator.AND ? 'AND' : 'OR';
	}

	@cached
	get filterItemsData(): DSSetup.MetricSetupFilterItemData[] {
		return this.filterItems.map((d) => d.filterItemData);
	}

	@cached
	get filterGroupData(): DSSetup.MetricSetupFilterGroupData {
		return {
			logicalOperator: this.logicalOperator,
			filterItems: this.filterItemsData,
		};
	}

	get filterItemsDimensions(): string[] {
		return compact(uniq(this.filterItems.map((item) => item.field)));
	}

	get isFull(): boolean {
		return this.filterItems.length >= MAX_FILTER_ITEMS;
	}

	getFilterItemsText(getDimensionNameForDimensionFn: (dimension: string) => string) {
		let text = '';
		this.filterItems.forEach((item, index) => {
			if (index) {
				text += `<br><span>${this.logicalOperatorText}</span> `;
			}
			const dimensionName = getDimensionNameForDimensionFn(item.field);
			text += `<span>${dimensionName}:</span> ${item.operatorText} <strong>${item.values.join(', ')}</strong>`;
		});
		return text;
	}

	isEqual(other: FilterGroup): boolean {
		return (
			this.logicalOperator === other.logicalOperator &&
			this.filterItems.every((item, index) => {
				return !!other.filterItems[index] && item.isEqual(other.filterItems[index]);
			})
		);
	}

	addFilterItem(item: FilterItem): void {
		this.filterItems = [...this.filterItems, item];
		this.notifyItemsChange();
	}

	removeFilterItem(item: FilterItem): void {
		this.filterItems = this.filterItems.filter((i) => i !== item);
		this.notifyItemsChange();
	}

	removeFilterItems(items: FilterItem[]): void {
		this.filterItems = this.filterItems.filter((i) => !items.includes(i));
		this.notifyItemsChange();
	}

	// --------- onChange notifier ----------

	private notifyItemsChange() {
		this.notifyChange('filterItems', this.filterItemsData);
	}

	protected notifyChange(prop: OnChangeProp, value: OnChangePropValue): void {
		if (this.onChange) {
			this.onChange(prop, value);
		}
	}
}
